import { useRef } from 'react';
import type { ReactNode } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { clsx } from 'clsx';
import {
  isMobile,
  shouldReduceMotion,
  getWillChangeStyle,
} from '@/lib/animations/optimized-animations';

export interface ParallaxSectionProps {
  /** Content rendered inside the parallax layer. */
  children?: ReactNode;
  /**
   * Parallax intensity. Higher values translate the layer further as it
   * scrolls through the viewport. Defaults to a subtle 0.3.
   */
  speed?: number;
  /** Optional extra classes merged onto the wrapper. */
  className?: string;
}

/**
 * ParallaxSection
 *
 * Scroll-linked parallax wrapper. Translates its content at a different rate
 * than the surrounding scroll (transform-only, GPU accelerated) for depth.
 * Falls back to a static layer on mobile or when reduced motion is preferred.
 */
export function ParallaxSection({
  children,
  speed = 0.3,
  className,
}: ParallaxSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const disabled = isMobile() || shouldReduceMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Map scroll progress (0 -> 1) to a transform offset. The magnitude scales
  // with `speed`; a 100px travel at speed 1 keeps the effect understated.
  const offset = Math.round(speed * 100);
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);

  if (disabled) {
    return (
      <div ref={ref} className={clsx('relative', className)}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={clsx('relative overflow-hidden', className)}>
      <motion.div style={{ y, ...getWillChangeStyle(['transform']) }}>
        {children}
      </motion.div>
    </div>
  );
}

export default ParallaxSection;
